import { userRepo } from '../repo';

export const userService = {
    async getAll(params = {}) {
        try {
            const response = await userRepo.getAll(params);
            return response;
        } catch (error) {
            throw this.handleError(error);
        }
    },

    async getAllUsersWithRoles(params = {}) { 
        try {
            const response = await userRepo.getAllUsersWithRoles(params);
            return response;
        } catch (error) {
            throw this.handleError(error);
        }
    },

    async getById(id) {
        if (!id) {
            throw new Error('User ID is required');
        }
        try {
            const response = await userRepo.getById(id);
            return response;
        } catch (error) {
            throw this.handleError(error);
        }
    },

    async getByEmail(email) {
        if (!email) {
            throw new Error('Email is required');
        }
        try {
            const response = await userRepo.getByEmail(email);
            return response;
        } catch (error) {
            throw this.handleError(error);
        }
    },

    async getByUsername(username) {
        if (!username) {
            throw new Error('Username is required');
        }
        try {
            const response = await userRepo.getByUsername(username);
            return response;
        } catch (error) {
            throw this.handleError(error);
        }
    },

    async create(data) {
        // Validate email
        if (!data.email || !data.email.includes('@')) {
            throw new Error('Valid email is required');
        }
        
        // Validate phone - accept +255 format or 9 digits
        if (!data.phone) {
            throw new Error('Phone number is required');
        }
        
        // Remove all non-digit characters for validation
        const cleanPhone = data.phone.replace(/\D/g, '');
        
        // Check if phone is valid: 
        // - 9 digits (e.g., 712345678) 
        // - 12 digits with country code without + (e.g., 255712345678)
        // - 13 digits with + (e.g., +255712345678) - already cleaned to 12 digits
        const isValidPhone = cleanPhone.length === 9 || cleanPhone.length === 12;
        
        if (!isValidPhone) {
            throw new Error('Valid phone number is required (9 digits or +255 format)');
        }
        
        // Validate password
        if (!data.password || data.password.length < 8) {
            throw new Error('Password must be at least 8 characters');
        }

        try {
            const response = await userRepo.create(data);
            return response;
        } catch (error) {
            throw this.handleError(error);
        }
    },

    async update(id, data) {
        if (!id) {
            throw new Error('User ID is required');
        }

        try {
            const response = await userRepo.update(id, data);
            return response;
        } catch (error) {
            throw this.handleError(error);
        }
    },

    async delete(id) {
        if (!id) {
            throw new Error('User ID is required');
        }

        try {
            const response = await userRepo.delete(id);
            return response;
        } catch (error) {
            throw this.handleError(error);
        }
    },

    // User Account only
    async activate(id) {
        if (!id) {
            throw new Error('User ID is required');
        }

        try {
            const response = await userRepo.activate(id);
            return response;
        } catch (error) {
            throw this.handleError(error);
        }
    },

    async deactivate(id) {
        if (!id) {
            throw new Error('User ID is required');
        }

        try {
            const response = await userRepo.deactivate(id);
            return response;
        } catch (error) {
            throw this.handleError(error);
        }
    },

    // User Login only
    async activateLogin(id) {
        if (!id) {
            throw new Error('User ID is required');
        }

        try {
            const response = await userRepo.activateLogin(id);
            return response;
        } catch (error) {
            throw this.handleError(error);
        }
    },

    async deactivateLogin(id) {
        if (!id) {
            throw new Error('User ID is required');
        }

        try {
            const response = await userRepo.deactivateLogin(id);
            return response;
        } catch (error) {
            throw this.handleError(error);
        }
    },

    // Both tables
    async activateBoth(id) {
        if (!id) {
            throw new Error('User ID is required');
        }

        try {
            const response = await userRepo.activateBoth(id);
            return response;
        } catch (error) {
            throw this.handleError(error);
        }
    },

    async deactivateBoth(id) {
        if (!id) {
            throw new Error('User ID is required');
        }

        try {
            const response = await userRepo.deactivateBoth(id);
            return response;
        } catch (error) {
            throw this.handleError(error);
        }
    },

    async changeRole(id, role) {
        if (!id) {
            throw new Error('User ID is required');
        }
        if (!role) {
            throw new Error('Role is required');
        }

        try {
            const response = await userRepo.changeRole(id, role);
            return response;
        } catch (error) {
            throw this.handleError(error);
        }
    },

       async changeUserPassword(id, newPassword) {
        if (!id) {
            throw new Error('User ID is required');
        }
        if (!newPassword || newPassword.length < 8) {
            throw new Error('Password must be at least 8 characters');
        }

        try {
            const response = await userRepo.changeUserPassword(
                id,
                newPassword,
            );
            return response;
        } catch (error) {
            throw this.handleError(error);
        }
    },
    async getPermissions(id) {
        if (!id) {
            throw new Error('User ID is required');
        }

        try {
            const response = await userRepo.getPermissions(id);
            return response;
        } catch (error) {
            throw this.handleError(error);
        }
    },

    async getStats() {
        try {
            const response = await userRepo.getAllUsersWithRoles({ per_page: 1000 });
            const users = response?.data || [];
            return {
                total: users.length,
                active: users.filter(u => u.status === 'active').length,
                inactive: users.filter(u => u.status === 'inactive').length,
                byRole: {
                    super_admin: users.filter(u => u.role === 'super_admin').length,
                    admin: users.filter(u => u.role === 'admin').length,
                    owner: users.filter(u => u.role === 'owner').length,
                    seller: users.filter(u => u.role === 'seller').length,
                },
            };
        } catch (error) {
            throw this.handleError(error);
        }
    },

    handleError(error) {
        console.error('UserService Error:', error);
        return error.response?.data || error;
    },
};